import { generatePath, Navigate, useLocation, useParams, RouteObject } from 'react-router-dom'
import { LcrSectionKey } from '@common/interfaces/generalAnalysis'
import { userRoleGroups } from '@common/interfaces/user'
import AuthLayout from './components/AuthLayout'
import NavigationSetup from './components/NavigationSetup'
import PrivateRoute from './components/PrivateRoute'
import { TERM_SHEET_SECTION_KEY } from './components/ProspectWriteup/sectionKeys'
import RouteRedirect from './components/RouteRedirect'
import { ROUTES } from './constants/routes'
import { lazyPage } from './helpers/routePreload'
import ErrorPage, { ErrorBoundary } from './pages/ErrorPage'

// Route-level code splitting: every page is a separate chunk so first paint no longer
// downloads all 129 pages. ErrorPage stays eager — an error boundary must not suspend.
const AccountActivityPage = lazyPage(() => import('./pages/AccountActivityPage'))
const ActivityQueuePage = lazyPage(() => import('./pages/ActivityQueuePage'))
const AdminClientSubmitReportingPage = lazyPage(
  () => import('./pages/AdminClientSubmitReportingPage'),
)
const AmendmentAnalysisPage = lazyPage(() => import('./pages/AmendmentAnalysisPage'))
const AmendmentAnalysisSettingsPage = lazyPage(
  () => import('./pages/AmendmentAnalysisSettingsPage'),
)
const ApplicationPage = lazyPage(() => import('./pages/ApplicationPage'))
const AtlasAIAutomationsPage = lazyPage(() => import('./pages/AtlasAI/Automations'))
const AtlasAIDocsPage = lazyPage(() => import('./pages/AtlasAI/Docs'))
const AtlasAILayout = lazyPage(() => import('./pages/AtlasAI'))
const AtlasAIMemoriesPage = lazyPage(() => import('./pages/AtlasAI/Memories'))
const AtlasAIPromptsPage = lazyPage(() => import('./pages/AtlasAI/Prompts'))
const AtlasAISettingsPage = lazyPage(() => import('./pages/AtlasAI/Settings'))
const AtlasAIWorkflowsPage = lazyPage(() => import('./pages/AtlasAI/Workflows'))
const AuditPage = lazyPage(() => import('./pages/AuditPage'))
const BBCCollateralEligibilityPage = lazyPage(() => import('./pages/BBCCollateralEligibilityPage'))
const BBCSignSuccessPage = lazyPage(() => import('./pages/BBCSignSuccessPage'))
const BBCSummaryPage = lazyPage(() => import('./pages/BBCSummaryPage'))
const BDOHomepage = lazyPage(() => import('./pages/BDOHomepage'))
const BankTransactionsPage = lazyPage(() => import('./pages/BankTransactionsPage'))
const BlankPage = lazyPage(() => import('./pages/BlankPage'))
const CRMCampaignPage = lazyPage(() => import('./pages/CRMCampaignPage'))
const CRMCompanyPage = lazyPage(() => import('./pages/CRMCompanyPage'))
const CRMPage = lazyPage(() => import('./pages/CRMPage'))
const CRMPersonPage = lazyPage(() => import('./pages/CRMPersonPage'))
const CRMSettingsPage = lazyPage(() => import('./pages/CRMSettingsPage'))
const CapTablePage = lazyPage(() => import('./pages/CapTablePage'))
const CertificatesPage = lazyPage(() => import('./pages/OngoingReportingCertificatesPage'))
const ClientIntakeClaimPage = lazyPage(() => import('./pages/ClientIntakeClaimPage'))
const ClientIntakeHomePage = lazyPage(() => import('./pages/ClientIntakeHomePage'))
const ClientIntakePage = lazyPage(() => import('./pages/ClientIntakePage'))
const ClientManagementPage = lazyPage(() => import('./pages/ClientManagementPage'))
const ClientPage = lazyPage(() => import('./pages/ClientPage'))
const ClientSettingsPage = lazyPage(() => import('./pages/ClientSettingsPage'))
const ClientSetupApiKeysPage = lazyPage(() => import('./pages/ClientSetupApiKeysPage'))
const ClientSetupCommunicationsPage = lazyPage(
  () => import('./pages/ClientSetupCommunicationsPage'),
)
const ClientSetupBankAccountPage = lazyPage(() => import('./pages/ClientSetupBankAccountPage'))
const ClientSetupCollateralInventoryPage = lazyPage(
  () => import('./pages/ClientSetupCollateralInventoryPage'),
)
const ClientSetupCollateralPayablePage = lazyPage(
  () => import('./pages/ClientSetupCollateralPayablePage'),
)
const ClientSetupCollateralReceivablePage = lazyPage(
  () => import('./pages/ClientSetupCollateralReceivablePage'),
)
const ClientSetupDocumentsPage = lazyPage(() => import('./pages/ClientSetupDocumentsPage'))
const ClientSetupLoanStructureAssetBasedPage = lazyPage(
  () => import('./pages/ClientSetupLoanStructureAssetBasedPage'),
)
const ClientSetupLoanStructureOveradvancePage = lazyPage(
  () => import('./pages/ClientSetupLoanStructureOveradvancePage'),
)
const ClientSetupLoanStructureParticipantsPage = lazyPage(
  () => import('./pages/ClientSetupLoanStructureParticipantsPage'),
)
const ClientSetupLoanStructureTermPage = lazyPage(
  () => import('./pages/ClientSetupLoanStructureTermPage'),
)
const ClientSetupPage = lazyPage(() => import('./pages/ClientSetupPage'))
const ClientSetupRelationshipManagerPage = lazyPage(
  () => import('./pages/ClientSetupRelationshipManagerPage'),
)
const ClientSetupReportingPage = lazyPage(() => import('./pages/ClientSetupReportingPage'))
const ClientSetupTerminationPage = lazyPage(() => import('./pages/ClientSetupTerminationPage'))
const ClientSetupUsersPage = lazyPage(() => import('./pages/ClientSetupUsersPage'))
const ClientVisualizationsPage = lazyPage(() => import('./pages/ClientVisualizationsPage'))
const CollectionsChecksPage = lazyPage(() => import('./pages/CollectionsChecksPage'))
const CollectionsCheckInvoiceReviewPage = lazyPage(
  () => import('./pages/CollectionsCheckInvoiceReviewPage'),
)
const CollectionsDashboardPage = lazyPage(() => import('./pages/CollectionsDashboardPage'))
const CollectionsMidDayWiresPage = lazyPage(() => import('./pages/CollectionsMidDayWiresPage'))
const CollectionsWiresPage = lazyPage(() => import('./pages/CollectionsWiresPage'))
const ConfirmEmail = lazyPage(() => import('./pages/ConfirmEmail'))
const CustomFieldsPage = lazyPage(() => import('./pages/CustomFieldsPage'))
const ReportsPage = lazyPage(() => import('./pages/ReportsPage'))
const ReportPage = lazyPage(() => import('./pages/ReportPage'))
const DashboardPage = lazyPage(() => import('./pages/DashboardPage'))
const DocumentEditorPage = lazyPage(() => import('./pages/DocumentEditorPage'))
const DocumentsPage = lazyPage(() => import('./pages/DocumentsPage'))
const DueDiligenceCapTableMappingPage = lazyPage(
  () => import('./pages/DueDiligenceCapTableMappingPage'),
)
const DueDiligenceClientFinancialsPage = lazyPage(
  () => import('./pages/DueDiligenceClientFinancialsPage'),
)
const DueDiligenceOverviewPage = lazyPage(() => import('./pages/DueDiligenceOverviewPage'))
const DueDiligenceProcessDocumentsBankTransactionsPage = lazyPage(
  () => import('./pages/DueDiligenceProcessDocumentsBankTransactionsPage'),
)
const DueDiligenceProcessDocumentsFinancialsPage = lazyPage(
  () => import('./pages/DueDiligenceProcessDocumentsFinancialsPage'),
)
const DueDiligenceProcessDocumentsGeneralLedgerPage = lazyPage(
  () => import('./pages/DueDiligenceProcessDocumentsGeneralLedgerPage'),
)
const DueDiligenceProcessDocumentsInventoryPage = lazyPage(
  () => import('./pages/DueDiligenceProcessDocumentsInventoryPage'),
)
const DueDiligenceProcessDocumentsListPage = lazyPage(
  () => import('./pages/DueDiligenceProcessDocumentsListPage'),
)
const DueDiligenceProcessDocumentsMappingPage = lazyPage(
  () => import('./pages/DueDiligenceProcessDocumentsMappingPage'),
)
const DueDiligenceProcessDocumentsPage = lazyPage(
  () => import('./pages/DueDiligenceProcessDocumentsPage'),
)
const DueDiligenceSalesBySKUPage = lazyPage(() => import('./pages/DueDiligenceSalesBySKUPage'))
const DueDiligenceValidateDocumentsPage = lazyPage(
  () => import('./pages/DueDiligenceValidateDocumentsPage'),
)
const ErrorCenterPage = lazyPage(() => import('./pages/ErrorCenterPage'))
const EntitySettingsAliasMapping = lazyPage(() => import('./pages/EntitySettingsAliasMapping'))
const EntitySettingsGeneralPage = lazyPage(() => import('./pages/EntitySettingsGeneralPage'))
const EntitySettingsPage = lazyPage(() => import('./pages/EntitySettingsPage'))
const EntitySettingsPayablesPage = lazyPage(() => import('./pages/EntitySettingsPayablesPage'))
const EntitySettingsReceivablesPage = lazyPage(
  () => import('./pages/EntitySettingsReceivablesPage'),
)
const EntitySettingsRelatedEntitiesPage = lazyPage(
  () => import('./pages/EnititySettingsRelatedEntitiesPage'),
)
const ExternalApiDocsPage = lazyPage(() => import('./pages/ExternalApiDocsPage'))
const Funding = lazyPage(() => import('./pages/RequestFundingPage'))
const GlobalSearchPage = lazyPage(() => import('./pages/GlobalSearchPage'))
const InsurancePage = lazyPage(() => import('./pages/OngoingReportingInsurancePage'))
const LoanCommitteeReportPage = lazyPage(() => import('./pages/LoanCommitteeReportPage'))
const ExportsDashboard = lazyPage(() => import('./components/ExportsDashboard'))
const UWDashboard = lazyPage(() => import('./components/UWDashboard'))
const LoginErrorPage = lazyPage(() => import('./pages/LoginErrorPage'))
const LoginPage = lazyPage(() => import('./pages/LoginPage'))
const MarketIntelligenceRoute = lazyPage(
  () => import('./pages/MarketNewsPage/MarketIntelligenceRoute'),
)
const MarketNewsPage = lazyPage(() => import('./pages/MarketNewsPage'))
const MessagesPage = lazyPage(() => import('./pages/Messages/MessagesPage'))
const MyFeedbackPage = lazyPage(() => import('./pages/MyFeedbackPage'))
const MyTasksPage = lazyPage(() => import('./pages/MyTasksPage'))
const CalendarPage = lazyPage(() => import('./pages/CalendarPage'))
const NotificationPage = lazyPage(() => import('./pages/NotificationPage'))
const OngoingReportingARGeneralLedgerPage = lazyPage(
  () => import('./pages/OngoingReportingARGeneralLedgerPage'),
)
const OngoingReportingPage = lazyPage(() => import('./pages/OngoingReportingPage'))
const OngoingReportingSalesBySKUPage = lazyPage(
  () => import('./pages/OngoingReportingSalesBySKUPage'),
)
const OrganizationPage = lazyPage(() => import('./pages/OrganizationPage'))
const OutgoingWirePage = lazyPage(() => import('./pages/OutgoingWirePage'))
const ParticipantManagementPage = lazyPage(() => import('./pages/ParticipantManagementPage'))
const ParticipantPage = lazyPage(() => import('./pages/ParticipantPage'))
const ParticipantSettingsPage = lazyPage(() => import('./pages/ParticipantSettingsPage'))
const PasswordRecoveryPage = lazyPage(() => import('./pages/PasswordRecoveryPage'))
const PasswordSetUpPage = lazyPage(() => import('./pages/PasswordSetUpPage'))
const PortfolioSettingsPage = lazyPage(() => import('./pages/PortfolioSettingsPage'))
const ProspectAddTermsPage = lazyPage(() => import('./pages/ProspectAddTermsPage'))
const ProspectAnalysisQueuePage = lazyPage(() => import('./pages/ProspectAnalysisQueuePage'))
const ProspectApplicationPage = lazyPage(() => import('./pages/ProspectApplicationPage'))
const ProspectOverviewPage = lazyPage(() => import('./pages/ProspectOverviewPage'))
const ProspectPage = lazyPage(() => import('./pages/ProspectPage'))
const ProspectReportingCapTablePage = lazyPage(
  () => import('./pages/ProspectReportingCapTablePage'),
)
const ProspectReportingFinancialsPage = lazyPage(
  () => import('./pages/ProspectReportingFinancialsPage'),
)
const ProspectReportingInventoryPage = lazyPage(
  () => import('./pages/ProspectReportingInventoryPage'),
)
const ProspectReportingPayablesPage = lazyPage(
  () => import('./pages/ProspectReportingPayablesPage'),
)
const ProspectReportingReceivablesPage = lazyPage(
  () => import('./pages/ProspectReportingReceivablesPage'),
)
const ProspectSettingsUsersPage = lazyPage(() => import('./pages/ProspectSettingsUsersPage'))
const ProspectSetupPage = lazyPage(() => import('./pages/ProspectSetupPage'))
const ProspectSummaryPage = lazyPage(() => import('./pages/ProspectSummaryPage'))
const QboRedirectPage = lazyPage(() => import('./pages/QboRedirectPage'))
const RedirectPage = lazyPage(() => import('./pages/CodatRedirectPage'))
const ResetPasswordPage = lazyPage(() => import('./pages/ResetPasswordPage'))
const SubmitLegalReportingPage = lazyPage(() => import('./pages/SubmitLegalReportingPage'))
const SubmitReportingPage = lazyPage(() => import('./pages/SubmitReportingPage'))
const TaskProjectPage = lazyPage(() => import('./pages/TaskProjectPage'))
const TaskProjectsPage = lazyPage(() => import('./pages/TaskProjectsPage'))
const UWSettingsPage = lazyPage(() => import('./pages/UWSettingsPage'))
const TeamsPage = lazyPage(() => import('./pages/TeamsPage'))
const UserManagementPage = lazyPage(() => import('./pages/UserManagementPage'))
const UserProfileSettingsPage = lazyPage(() => import('./pages/UserProfileSettingsPage'))
const WrongMatchReportPage = lazyPage(() => import('./pages/WrongMatchReportPage'))
// Auth page imports

const OngoingReportingRedirect = () => {
  const { id } = useParams<{ id: string }>()
  const redirectPath = generatePath(ROUTES.ONGOING_REPORTING_INCOME_STATEMENT, {
    id,
  })
  return <Navigate to={redirectPath} replace />
}

/**
 * The retired `/atlas-ai/chat` route, forwarding to Messages *with its query
 * string*. `<Navigate to={ROUTES.MESSAGES}>` drops the search, which silently
 * broke every `?share=<token>` invite link that had already gone out.
 */
const RedirectToMessages = () => {
  const { search } = useLocation()
  return <Navigate to={`${ROUTES.MESSAGES}${search}`} replace />
}

/**
 * A retired per-tab route, forwarding to the single scrolling page that absorbed
 * it, anchored at the section it used to be — the Loan Committee Report's eight
 * tabs, and the prospect Term Sheet tab the Deal Summary absorbed. Bookmarks,
 * emailed links and notification links all still land in the right place, and
 * the query string is preserved so a `?scrollTo=<noteId>` survives the hop.
 */
const SectionAnchorRedirect = ({ route, sectionKey }: { route: string; sectionKey: string }) => {
  const { id } = useParams<{ id: string }>()
  const { search } = useLocation()
  const path = generatePath(route, { id })
  return <Navigate to={`${path}${search}#section-${sectionKey}`} replace />
}

/** The Loan Committee Report's eight retired per-tab routes. */
const LcrSectionRedirect = ({ sectionKey }: { sectionKey: LcrSectionKey }) => (
  <SectionAnchorRedirect
    route={ROUTES.DUE_DILIGENCE_LOAN_COMMITTEE_REPORT_PAGE}
    sectionKey={sectionKey}
  />
)

// Create route objects for the new router
// A pre-login intake link is the intake path itself; its claim page hangs off it.
const intakeClaimPath = (pathname: string) => `${pathname.replace(/\/$/, '')}/claim`

export const createAppRoutes = (): RouteObject[] => {
  return [
    {
      path: '/',
      element: <NavigationSetup />,
      errorElement: <ErrorBoundary />,
      children: [
        {
          path: ROUTES.MY_TASKS,
          element: <PrivateRoute component={MyTasksPage} />,
        },
        {
          path: ROUTES.CALENDAR,
          element: <PrivateRoute component={CalendarPage} allowedRoles={userRoleGroups.INTERNAL} />,
        },
        {
          path: ROUTES.MESSAGES,
          element: <PrivateRoute component={MessagesPage} />,
        },
        {
          // Same page component; the :channelId param is what opens a
          // conversation, so a deep link and a sidebar click take one path.
          path: ROUTES.MESSAGES_CHANNEL,
          element: <PrivateRoute component={MessagesPage} />,
        },
        {
          // The homepages are the same page too — they render in the main pane
          // beside the sidebar, the way the Threads inbox does.
          path: ROUTES.MESSAGES_CHANNEL_HOME,
          element: <PrivateRoute component={MessagesPage} />,
        },
        {
          path: ROUTES.MESSAGES_PERSON_HOME,
          element: <PrivateRoute component={MessagesPage} />,
        },
        {
          path: ROUTES.MESSAGES_ARC_HOME,
          element: <PrivateRoute component={MessagesPage} />,
        },
        {
          path: ROUTES.TASK_PROJECTS,
          element: <PrivateRoute component={TaskProjectsPage} />,
        },
        {
          path: ROUTES.TASK_PROJECT_PAGE,
          element: <PrivateRoute component={TaskProjectPage} />,
        },
        {
          path: ROUTES.CUSTOM_FIELDS,
          element: <PrivateRoute component={CustomFieldsPage} />,
        },
        {
          path: ROUTES.TEAMS,
          element: <PrivateRoute component={TeamsPage} />,
        },
        {
          path: ROUTES.HOMEPAGE,
          element: <PrivateRoute component={DashboardPage} />,
        },
        {
          path: ROUTES.ATLAS_AI,
          element: <PrivateRoute component={AtlasAILayout} />,
          children: [
            // The section is Arc's configuration now — chatting with it happens
            // in Messages.
            { index: true, element: <Navigate to={ROUTES.ATLAS_AI_AUTOMATIONS} replace /> },
            // Kept as a redirect rather than deleted: /atlas-ai/chat is in
            // bookmarks, Slack messages and old share links, and a dead route
            // there reads as the feature being gone rather than moved.
            //
            // Carries the query string across, which a bare `<Navigate to>` drops.
            // That mattered: every share link ever emailed out is
            // `/atlas-ai/chat?share=<token>`, and losing the token turned a live
            // invite into a landing on an empty Messages page.
            { path: 'chat', element: <RedirectToMessages /> },
            { path: 'docs', element: <AtlasAIDocsPage /> },
            { path: 'memories', element: <AtlasAIMemoriesPage /> },
            { path: 'prompts', element: <AtlasAIPromptsPage /> },
            { path: 'workflows', element: <AtlasAIWorkflowsPage /> },
            { path: 'automations', element: <AtlasAIAutomationsPage /> },
            { path: 'settings', element: <AtlasAISettingsPage /> },
          ],
        },
        {
          path: ROUTES.MY_FEEDBACK,
          element: <PrivateRoute component={MyFeedbackPage} />,
        },
        {
          path: ROUTES.ERROR_CENTER,
          element: (
            <PrivateRoute component={ErrorCenterPage} allowedRoles={userRoleGroups.ERROR_CENTER} />
          ),
        },
        {
          path: ROUTES.SETTINGS,
          element: <PrivateRoute component={ClientSettingsPage} />,
        },
        {
          path: ROUTES.SETTINGS_PROFILE,
          element: <PrivateRoute component={UserProfileSettingsPage} />,
        },
        {
          path: ROUTES.SETTINGS_SECURITY,
          element: <PrivateRoute component={UserProfileSettingsPage} />,
        },
        {
          path: ROUTES.CLIENT_SETTINGS_NOTIFICATIONS,
          element: <PrivateRoute component={UserProfileSettingsPage} />,
        },
        {
          path: ROUTES.SETTINGS_DISPLAY,
          element: <PrivateRoute component={UserProfileSettingsPage} />,
        },
        {
          path: ROUTES.SETTINGS_ACCESS_TOKENS,
          element: <PrivateRoute component={UserProfileSettingsPage} />,
        },
        {
          path: ROUTES.PORTFOLIO_SETTINGS,
          element: <PrivateRoute component={PortfolioSettingsPage} />,
        },
        {
          path: ROUTES.CLIENT_MANAGEMENT,
          element: <PrivateRoute component={ClientManagementPage} />,
        },
        {
          path: ROUTES.USER_MANAGEMENT,
          element: <PrivateRoute component={UserManagementPage} />,
        },
        {
          path: ROUTES.AUDIT,
          element: <PrivateRoute component={AuditPage} />,
        },
        {
          path: ROUTES.CLIENT_SETUP_CREATE,
          element: <PrivateRoute component={ClientSetupLoanStructureAssetBasedPage} />,
        },
        {
          path: ROUTES.CLIENT_SETUP,
          element: <PrivateRoute component={ClientSetupPage} />,
        },
        {
          path: ROUTES.CLIENT_SETUP_LOAN_STRUCTURE_ASSET_BASED,
          element: <PrivateRoute component={ClientSetupLoanStructureAssetBasedPage} />,
        },
        {
          path: ROUTES.CLIENT_SETUP_LOAN_STRUCTURE_TERM,
          element: <PrivateRoute component={ClientSetupLoanStructureTermPage} />,
        },
        {
          path: ROUTES.CLIENT_SETUP_LOAN_STRUCTURE_OVERADVANCE,
          element: <PrivateRoute component={ClientSetupLoanStructureOveradvancePage} />,
        },
        {
          path: ROUTES.CLIENT_SETUP_LOAN_STRUCTURE_PARTICIPANTS,
          element: <PrivateRoute component={ClientSetupLoanStructureParticipantsPage} />,
        },
        {
          path: ROUTES.PARTICIPANTS_MANAGEMENT,
          element: <PrivateRoute component={ParticipantManagementPage} />,
        },
        {
          path: ROUTES.PARTICIPANTS_PAGE,
          element: <PrivateRoute component={ParticipantPage} />,
        },
        {
          path: ROUTES.PARTICIPANT_SETTINGS,
          element: <PrivateRoute component={ParticipantSettingsPage} />,
        },
        {
          path: ROUTES.CLIENT_SETUP_COLLATERAL_RECEIVABLE,
          element: <PrivateRoute component={ClientSetupCollateralReceivablePage} />,
        },
        {
          path: ROUTES.CLIENT_SETUP_COLLATERAL_PAYABLE,
          element: <PrivateRoute component={ClientSetupCollateralPayablePage} />,
        },
        {
          path: ROUTES.CLIENT_SETUP_COLLATERAL_INVENTORY,
          element: <PrivateRoute component={ClientSetupCollateralInventoryPage} />,
        },
        {
          path: ROUTES.CLIENT_SETUP_REPORTING,
          element: <PrivateRoute component={ClientSetupReportingPage} />,
        },
        {
          path: ROUTES.CLIENT_SETUP_BANK_ACCOUNTS,
          element: <PrivateRoute component={ClientSetupBankAccountPage} />,
        },
        {
          path: ROUTES.CLIENT_SETUP_USERS,
          element: <PrivateRoute component={ClientSetupUsersPage} />,
        },
        {
          path: ROUTES.CLIENT_SETUP_RELATIONSHIP_MANAGEMENT,
          element: <PrivateRoute component={ClientSetupRelationshipManagerPage} />,
        },
        {
          path: ROUTES.CLIENT_SETUP_TERMINATION,
          element: <PrivateRoute component={ClientSetupTerminationPage} />,
        },
        {
          path: ROUTES.CLIENT_SETUP_API_KEYS,
          element: <PrivateRoute component={ClientSetupApiKeysPage} />,
        },
        {
          path: ROUTES.CLIENT_SETUP_COMMUNICATIONS,
          element: <PrivateRoute component={ClientSetupCommunicationsPage} />,
        },
        {
          path: ROUTES.CLIENT_SETUP_PHONE_NUMBERS,
          element: <RouteRedirect to={ROUTES.CLIENT_SETUP_COMMUNICATIONS} />,
        },
        {
          path: ROUTES.EXTERNAL_API_DOCS,
          element: <PrivateRoute component={ExternalApiDocsPage} />,
        },
        {
          path: ROUTES.CLIENT_SETUP_GENERAL,
          element: <PrivateRoute component={ClientSetupDocumentsPage} />,
        },
        {
          path: ROUTES.COLLECTIONS_DASHBOARD,
          element: <PrivateRoute component={CollectionsDashboardPage} />,
        },
        {
          path: ROUTES.COLLECTIONS_DASHBOARD_OVERALL,
          element: <PrivateRoute component={CollectionsDashboardPage} />,
        },
        {
          path: ROUTES.COLLECTIONS_DASHBOARD_NON_CLIENT_RELATED_CASH,
          element: <PrivateRoute component={CollectionsDashboardPage} />,
        },
        {
          path: ROUTES.COLLECTIONS_WIRES,
          element: <PrivateRoute component={CollectionsWiresPage} />,
        },
        {
          path: ROUTES.COLLECTIONS_MID_DAY_WIRES,
          element: <PrivateRoute component={CollectionsMidDayWiresPage} />,
        },
        {
          path: ROUTES.COLLECTIONS_CHECKS,
          element: <PrivateRoute component={CollectionsChecksPage} />,
        },
        {
          path: ROUTES.COLLECTIONS_CHECK_INVOICE_REVIEW,
          element: <PrivateRoute component={CollectionsCheckInvoiceReviewPage} />,
        },
        {
          path: ROUTES.ACTIVITY_QUEUE,
          element: <PrivateRoute component={ActivityQueuePage} />,
        },
        {
          path: ROUTES.OUTGOING_WIRE,
          element: <PrivateRoute component={OutgoingWirePage} />,
        },
        {
          path: ROUTES.BBC_COLLATERAL_ELIGIBILITY,
          element: <PrivateRoute component={BBCCollateralEligibilityPage} />,
        },
        {
          path: ROUTES.BBC_COLLATERAL_ELIGIBILITY_RECEIVABLES,
          element: <PrivateRoute component={BBCCollateralEligibilityPage} />,
        },
        {
          path: ROUTES.BBC_COLLATERAL_ELIGIBILITY_INVENTORY,
          element: <PrivateRoute component={BBCCollateralEligibilityPage} />,
        },
        {
          path: ROUTES.BBC_SUMMARY,
          element: <PrivateRoute component={BBCSummaryPage} />,
        },
        {
          path: ROUTES.BBC_SUMMARY_HISTORICAL,
          element: <PrivateRoute component={BBCSummaryPage} />,
        },
        {
          path: ROUTES.BBC_SUMMARY_RECEIVABLES,
          element: <PrivateRoute component={BBCSummaryPage} />,
        },
        {
          path: ROUTES.BBC_SUMMARY_PAYABLES,
          element: <PrivateRoute component={BBCSummaryPage} />,
        },
        {
          path: ROUTES.BBC_SUMMARY_INVENTORY,
          element: <PrivateRoute component={BBCSummaryPage} />,
        },
        {
          path: ROUTES.ONGOING_REPORTING_SALES_BY_SKU,
          element: <PrivateRoute component={OngoingReportingSalesBySKUPage} />,
        },
        {
          path: ROUTES.ONGOING_REPORTING_AR_GENERAL_LEDGER,
          element: <PrivateRoute component={OngoingReportingARGeneralLedgerPage} />,
        },
        {
          path: ROUTES.ONGOING_REPORTING_BALANCE_SHEET,
          element: <PrivateRoute component={OngoingReportingPage} />,
        },
        {
          path: ROUTES.ONGOING_REPORTING_INCOME_STATEMENT,
          element: <PrivateRoute component={OngoingReportingPage} />,
        },
        {
          path: ROUTES.ONGOING_REPORTING_SUMMARY,
          element: <PrivateRoute component={OngoingReportingPage} />,
        },
        {
          path: ROUTES.ONGOING_REPORTING,
          element: <OngoingReportingRedirect />,
        },
        {
          path: ROUTES.ACCOUNT_ACTIVITY,
          element: <PrivateRoute component={AccountActivityPage} />,
        },
        {
          path: ROUTES.SUBMIT_REPORTING,
          element: <PrivateRoute component={SubmitReportingPage} />,
        },
        {
          path: ROUTES.CLIENT_VISUALIZATIONS,
          element: <PrivateRoute component={ClientVisualizationsPage} />,
        },
        {
          path: ROUTES.APPLICATION_PAGE,
          element: <PrivateRoute component={ApplicationPage} />,
        },
        {
          path: ROUTES.DOCUMENTS_PAGE,
          element: <PrivateRoute component={DocumentsPage} />,
        },
        {
          path: ROUTES.COMPANY,
          element: <PrivateRoute component={BlankPage} />,
        },
        {
          path: ROUTES.CLIENT_SUBMIT_REPORTING_PAGE,
          element: <PrivateRoute component={AdminClientSubmitReportingPage} />,
        },
        {
          path: ROUTES.CLIENT_SUBMIT_LEGAL_REPORTING_PAGE,
          element: <PrivateRoute component={SubmitLegalReportingPage} />,
        },
        {
          path: ROUTES.CLIENT_PAGE,
          element: <PrivateRoute component={ClientPage} />,
        },
        {
          path: ROUTES.AMENDMENT_ANALYSIS,
          element: <PrivateRoute component={AmendmentAnalysisPage} />,
        },
        {
          path: ROUTES.CLIENTS,
          element: <PrivateRoute component={BlankPage} />,
        },
        {
          path: ROUTES.ONGOING_REPORTING_BANK_TRANSACTIONS_UPLOAD,
          element: <PrivateRoute component={BankTransactionsPage} />,
        },
        {
          path: ROUTES.ONGOING_REPORTING_CAP_TABLE_UPLOAD,
          element: <PrivateRoute component={CapTablePage} />,
        },
        {
          path: ROUTES.ONGOING_REPORTING_INSURANCE_PAGE,
          element: <PrivateRoute component={InsurancePage} />,
        },
        {
          path: ROUTES.ONGOING_REPORTING_CERTIFICATES_PAGE,
          element: <PrivateRoute component={CertificatesPage} />,
        },
        {
          path: ROUTES.PROSPECT_SETUP,
          element: <PrivateRoute component={ProspectSetupPage} />,
        },
        {
          path: ROUTES.PROSPECTS,
          element: <PrivateRoute component={BDOHomepage} />,
        },
        {
          path: ROUTES.REPORTS,
          element: <PrivateRoute component={ReportsPage} allowedRoles={userRoleGroups.INTERNAL} />,
        },
        {
          path: ROUTES.REPORT_PAGE,
          element: <PrivateRoute component={ReportPage} allowedRoles={userRoleGroups.INTERNAL} />,
        },
        {
          path: ROUTES.PROSPECT_PAGE,
          element: <PrivateRoute component={ProspectPage} />,
        },
        {
          path: ROUTES.PROSPECT_OVERVIEW_PAGE,
          element: <PrivateRoute component={ProspectOverviewPage} />,
        },
        {
          path: ROUTES.PROSPECT_APPLICATION_PAGE,
          element: <PrivateRoute component={ProspectApplicationPage} />,
        },
        {
          path: ROUTES.PROSPECT_ANALYSIS_QUEUE,
          element: <PrivateRoute component={ProspectAnalysisQueuePage} />,
        },
        {
          path: ROUTES.PROSPECT_SUMMARY_PAGE,
          element: <PrivateRoute component={ProspectSummaryPage} />,
        },
        {
          path: ROUTES.CODAT_REDIRECT_PAGE,
          element: <PrivateRoute component={RedirectPage} />,
        },
        {
          path: ROUTES.QBO_REDIRECT_PAGE,
          element: <PrivateRoute component={QboRedirectPage} />,
        },
        {
          path: ROUTES.PROSPECT_REPORTING_RECEIVABLES,
          element: <PrivateRoute component={ProspectReportingReceivablesPage} />,
        },
        {
          path: ROUTES.PROSPECT_REPORTING_PAYABLES,
          element: <PrivateRoute component={ProspectReportingPayablesPage} />,
        },
        {
          path: ROUTES.PROSPECT_REPORTING_INVENTORY,
          element: <PrivateRoute component={ProspectReportingInventoryPage} />,
        },
        {
          path: ROUTES.PROSPECT_REPORTING_INCOME_STATEMENT,
          element: <PrivateRoute component={ProspectReportingFinancialsPage} />,
        },
        {
          path: ROUTES.PROSPECT_REPORTING_BALANCE_SHEET,
          element: <PrivateRoute component={ProspectReportingFinancialsPage} />,
        },
        {
          path: ROUTES.PROSPECT_REPORTING_INCOME_STATEMENT_PROJECTIONS,
          element: <PrivateRoute component={ProspectReportingFinancialsPage} />,
        },
        {
          path: ROUTES.PROSPECT_REPORTING_BALANCE_SHEET_PROJECTIONS,
          element: <PrivateRoute component={ProspectReportingFinancialsPage} />,
        },
        {
          path: ROUTES.PROSPECT_REPORTING_CAP_TABLE,
          element: <PrivateRoute component={ProspectReportingCapTablePage} />,
        },
        {
          path: ROUTES.PROSPECT_ADD_TERMS_PAGE,
          element: <PrivateRoute component={ProspectAddTermsPage} />,
        },
        {
          path: ROUTES.PROSPECT_EDIT_TERMS_PAGE,
          element: <PrivateRoute component={ProspectAddTermsPage} />,
        },
        {
          path: ROUTES.PROSPECT_SETTINGS_USERS_PAGE,
          element: <PrivateRoute component={ProspectSettingsUsersPage} />,
        },
        // The intake now lives inside the authenticated shell — same sidebar and
        // header as the client portal — rather than the anonymous AuthLayout.
        {
          path: ROUTES.CLIENT_INTAKE_HOME,
          element: <PrivateRoute component={ClientIntakeHomePage} />,
        },
        // Pre-login intake links land here with no session. Send those visitors
        // to the claim page rather than the login form — they have no account
        // yet, so logging in isn't an option they can take.
        {
          path: ROUTES.CLIENT_INTAKE_CLAIM,
          element: (
            <AuthLayout>
              <ClientIntakeClaimPage />
            </AuthLayout>
          ),
        },
        {
          path: ROUTES.CLIENT_INTAKE,
          element: (
            <PrivateRoute component={ClientIntakePage} unauthenticatedRedirect={intakeClaimPath} />
          ),
        },
        {
          path: ROUTES.PROSPECT_MANAGE_TERMS_PAGE,
          element: (
            <SectionAnchorRedirect
              route={ROUTES.PROSPECT_SUMMARY_PAGE}
              sectionKey={TERM_SHEET_SECTION_KEY}
            />
          ),
        },
        {
          path: ROUTES.DUE_DILIGENCE_HOME,
          element: <PrivateRoute component={UWDashboard} />,
        },
        {
          path: ROUTES.DUE_DILIGENCE_SETTINGS,
          element: <PrivateRoute component={UWSettingsPage} />,
        },
        {
          path: ROUTES.AMENDMENT_ANALYSIS_SETTINGS,
          element: <PrivateRoute component={AmendmentAnalysisSettingsPage} />,
        },
        {
          // DD landing now opens the company Overview (Background/Team retired as
          // tabs — their submitted values live in the "View submitted application"
          // modal, the live data on the Overview). Every DD deep link across the
          // app points here, so it stays the canonical /due-diligence/:id route.
          path: ROUTES.DUE_DILIGENCE,
          element: <PrivateRoute component={DueDiligenceOverviewPage} />,
        },
        {
          path: ROUTES.DUE_DILIGENCE_OVERVIEW,
          element: <PrivateRoute component={DueDiligenceOverviewPage} />,
        },
        {
          path: ROUTES.DUE_DILIGENCE_CLIENT_FINANCIALS_PAGE,
          element: <PrivateRoute component={DueDiligenceClientFinancialsPage} />,
        },
        {
          path: ROUTES.DUE_DILIGENCE_ANALYSIS_QUEUE,
          element: <PrivateRoute component={DueDiligenceProcessDocumentsPage} />,
        },
        {
          path: ROUTES.DUE_DILIGENCE_VALIDATE_DOCUMENTS_PAGE,
          element: <PrivateRoute component={DueDiligenceValidateDocumentsPage} />,
        },
        {
          path: ROUTES.DUE_DILIGENCE_PROCESS_DOCUMENTS_LIST,
          element: <PrivateRoute component={DueDiligenceProcessDocumentsListPage} />,
        },
        {
          path: ROUTES.DUE_DILIGENCE_PROCESS_DOCUMENTS_MAPPING,
          element: <PrivateRoute component={DueDiligenceProcessDocumentsMappingPage} />,
        },
        {
          path: ROUTES.DUE_DILIGENCE_PROCESS_DOCUMENTS_FINANCIALS,
          element: <PrivateRoute component={DueDiligenceProcessDocumentsFinancialsPage} />,
        },
        {
          path: ROUTES.DUE_DILIGENCE_PROCESS_DOCUMENTS_BANK_TRANSACTIONS,
          element: <PrivateRoute component={DueDiligenceProcessDocumentsBankTransactionsPage} />,
        },
        {
          path: ROUTES.DUE_DILIGENCE_PROCESS_DOCUMENTS_GENERAL_LEDGER,
          element: <PrivateRoute component={DueDiligenceProcessDocumentsGeneralLedgerPage} />,
        },
        {
          path: ROUTES.DUE_DILIGENCE_PROCESS_DOCUMENTS_INVENTORY,
          element: <PrivateRoute component={DueDiligenceProcessDocumentsInventoryPage} />,
        },
        {
          path: ROUTES.DUE_DILIGENCE_PROCESS_DOCUMENTS_SALES_BY_SKU,
          element: <PrivateRoute component={DueDiligenceSalesBySKUPage} />,
        },
        {
          path: ROUTES.DUE_DILIGENCE_PROCESS_DOCUMENTS_CAP_TABLE,
          element: <PrivateRoute component={DueDiligenceCapTableMappingPage} />,
        },
        {
          path: ROUTES.DUE_DILIGENCE_LOAN_COMMITTEE_REPORT_PAGE,
          element: <PrivateRoute component={LoanCommitteeReportPage} />,
        },
        {
          path: ROUTES.DUE_DILIGENCE_LOAN_COMMITTEE_REPORT_FINANCIALS_PAGE,
          element: <LcrSectionRedirect sectionKey={LcrSectionKey.Financials} />,
        },
        {
          path: ROUTES.DUE_DILIGENCE_LOAN_COMMITTEE_REPORT_FORECAST_PAGE,
          element: <LcrSectionRedirect sectionKey={LcrSectionKey.Forecast} />,
        },
        {
          path: ROUTES.DUE_DILIGENCE_LOAN_COMMITTEE_REPORT_PAYABLES_PAGE,
          element: <LcrSectionRedirect sectionKey={LcrSectionKey.Payables} />,
        },
        {
          path: ROUTES.DUE_DILIGENCE_LOAN_COMMITTEE_REPORT_RECEIVABLES_PAGE,
          element: <LcrSectionRedirect sectionKey={LcrSectionKey.Receivables} />,
        },
        {
          path: ROUTES.FUNDING,
          element: <PrivateRoute component={Funding} />,
        },
        {
          path: ROUTES.DUE_DILIGENCE_LOAN_COMMITTEE_REPORT_INVENTORY_PAGE,
          element: <LcrSectionRedirect sectionKey={LcrSectionKey.Inventory} />,
        },
        {
          path: ROUTES.DUE_DILIGENCE_LOAN_COMMITTEE_REPORT_CAP_TABLE_PAGE,
          element: <LcrSectionRedirect sectionKey={LcrSectionKey.CapTable} />,
        },
        {
          path: ROUTES.DUE_DILIGENCE_LOAN_COMMITTEE_REPORT_TRANSACTIONS_PAGE,
          element: <LcrSectionRedirect sectionKey={LcrSectionKey.Transactions} />,
        },
        {
          path: ROUTES.DUE_DILIGENCE_LOAN_COMMITTEE_REPORT_FIELD_EXAM_PAGE,
          element: <LcrSectionRedirect sectionKey={LcrSectionKey.FieldExam} />,
        },
        {
          path: ROUTES.ORGANIZATION_PAGE,
          element: <PrivateRoute component={OrganizationPage} />,
        },
        {
          path: ROUTES.CRM_HOME,
          element: <PrivateRoute component={CRMPage} />,
        },
        // Declared before the :id record routes so /crm/settings isn't swallowed
        // by a sibling pattern.
        {
          path: ROUTES.CRM_SETTINGS,
          element: <PrivateRoute component={CRMSettingsPage} />,
        },
        // Legacy URLs now fold into the unified views.
        {
          path: ROUTES.ENTITY_PAGE,
          element: <RouteRedirect to={ROUTES.ORGANIZATION_PAGE} search={{ source: 'entity' }} />,
        },
        {
          path: ROUTES.ENTITY_MANAGEMENT,
          element: <Navigate to={ROUTES.CRM_HOME} replace />,
        },
        {
          path: ROUTES.CRM_ORGANIZATION_PAGE,
          element: (
            <RouteRedirect to={ROUTES.ORGANIZATION_PAGE} search={{ source: 'organization' }} />
          ),
        },
        {
          path: ROUTES.CRM_COMPANY_PAGE,
          element: <PrivateRoute component={CRMCompanyPage} />,
        },
        {
          path: ROUTES.CRM_PERSON_PAGE,
          element: <PrivateRoute component={CRMPersonPage} />,
        },
        {
          path: ROUTES.CRM_CAMPAIGN_PAGE,
          element: <PrivateRoute component={CRMCampaignPage} />,
        },
        {
          path: ROUTES.ENTITY_SETTINGS,
          element: <PrivateRoute component={EntitySettingsPage} />,
        },
        {
          path: ROUTES.ENTITY_SETTINGS_GENERAL,
          element: <PrivateRoute component={EntitySettingsGeneralPage} />,
        },
        {
          path: ROUTES.ENTITY_SETTINGS_RECEIVABLES,
          element: <PrivateRoute component={EntitySettingsReceivablesPage} />,
        },
        {
          path: ROUTES.ENTITY_SETTINGS_PAYABLES,
          element: <PrivateRoute component={EntitySettingsPayablesPage} />,
        },
        {
          path: ROUTES.ENTITY_SETTINGS_ALIAS_MAPPING,
          element: <PrivateRoute component={EntitySettingsAliasMapping} />,
        },
        {
          path: ROUTES.ENTITY_SETTINGS_RELATED_ENTITIES,
          element: <PrivateRoute component={EntitySettingsRelatedEntitiesPage} />,
        },
        {
          path: ROUTES.NOTIFICATIONS,
          element: <PrivateRoute component={NotificationPage} />,
        },
        {
          path: ROUTES.MARKET_NEWS,
          element: <PrivateRoute component={MarketNewsPage} />,
        },
        {
          path: ROUTES.MARKET_INTELLIGENCE,
          element: <PrivateRoute component={MarketIntelligenceRoute} />,
        },
        {
          path: ROUTES.NEWSFEED_WRONG_MATCH,
          element: (
            <AuthLayout>
              <WrongMatchReportPage />
            </AuthLayout>
          ),
        },
        {
          path: ROUTES.EXPORTS_PAGE,
          element: <PrivateRoute component={ExportsDashboard} />,
        },
        {
          path: ROUTES.GLOBAL_SEARCH,
          element: <PrivateRoute component={GlobalSearchPage} />,
        },
        {
          path: ROUTES.DOCUMENT_EDITOR,
          element: <PrivateRoute component={DocumentEditorPage} />,
        },

        // Auth routes (for non-authenticated users)

        {
          path: ROUTES.LOGIN,
          element: (
            <AuthLayout>
              <LoginPage />
            </AuthLayout>
          ),
        },
        {
          path: ROUTES.LOGIN_ERROR,
          element: (
            <AuthLayout>
              <LoginErrorPage />
            </AuthLayout>
          ),
        },
        {
          path: ROUTES.BBC_SIGN_SUCCESS,
          element: (
            <AuthLayout>
              <BBCSignSuccessPage />
            </AuthLayout>
          ),
        },
        {
          path: ROUTES.RESET_PASSWORD,
          element: (
            <AuthLayout>
              <ResetPasswordPage />
            </AuthLayout>
          ),
        },
        {
          path: ROUTES.PASSWORD_RECOVERY,
          element: (
            <AuthLayout>
              <PasswordRecoveryPage />
            </AuthLayout>
          ),
        },
        {
          path: ROUTES.CONFIRM_EMAIL,
          element: (
            <AuthLayout>
              <ConfirmEmail />
            </AuthLayout>
          ),
        },
        {
          path: ROUTES.PASSWORD_SETUP,
          element: (
            <AuthLayout>
              <PasswordSetUpPage />
            </AuthLayout>
          ),
        },
        {
          path: ROUTES.GLOBAL_ERROR,
          element: (
            <AuthLayout>
              <ErrorPage />
            </AuthLayout>
          ),
        },
        // Catch-all route for 404s
        {
          path: '*',
          element: (
            <AuthLayout>
              <ErrorPage source="not_found" />
            </AuthLayout>
          ),
        },
      ],
    },
  ]
}
